import { useEffect, useState } from 'react';
import {
  type DataEnvelope,
  type GuestOffer,
  guestOffersFetch,
  offerMissingText,
  offerRequirementText,
  offerStateLabel,
} from '../guestOffersApi';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icons';
import { HopperIcon } from '../ui/icons/HopperIcon';
import { ProgressBar } from '../ui/ProgressBar';
import '../offers.css';
import '../offers-experience-v1.css';

type OfferDetailScreenProps = {
  offerId: string;
};

function formattedEndDate(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function activationContext(status: string): { title: string; body: string } {
  switch (status) {
    case 'active':
      return {
        title: 'Предложение активировано',
        body: 'Для получения используйте уже существующий экран активированного предложения и код заведения.',
      };
    case 'redeemed':
      return { title: 'Предложение получено', body: 'Получение уже подтверждено заведением.' };
    case 'expired':
      return { title: 'Активация истекла', body: 'Эта активация больше недоступна.' };
    case 'revoked':
      return { title: 'Активация недоступна', body: 'Эта активация была отозвана.' };
    default:
      return { title: 'Статус активации', body: status };
  }
}

export function OfferDetailScreen({ offerId }: OfferDetailScreenProps) {
  const [offer, setOffer] = useState<GuestOffer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setOffer(null);

    void guestOffersFetch<DataEnvelope<GuestOffer>>(`/api/v1/offers/${encodeURIComponent(offerId)}`)
      .then((response) => {
        if (!cancelled) setOffer(response.data);
      })
      .catch(() => {
        if (!cancelled) setError('Предложение недоступно или больше не опубликовано.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [offerId]);

  const requirement = offer ? offerRequirementText(offer) : null;
  const missing = offer ? offerMissingText(offer) : null;
  const endDate = formattedEndDate(offer?.ends_at ?? null);
  const activation = offer?.my_activation ?? null;
  const activationCopy = activation ? activationContext(activation.status) : null;

  return (
    <main className="offers-page offer-detail-page" aria-label="Предложение Хоппера">
      <section className="offer-detail-main">
        <button className="social-back" type="button" onClick={() => window.history.length > 1 ? window.history.back() : window.location.assign('/offers')}>
          ← Назад
        </button>

        {loading ? (
          <div className="offers-state panel-card" role="status">
            <span className="map-state-card__pulse" />
            <strong>Загружаем предложение</strong>
            <span>Проверяем актуальные условия.</span>
          </div>
        ) : error || !offer ? (
          <div className="offers-state panel-card">
            <HopperIcon name="gift" />
            <strong>{error ?? 'Предложение недоступно.'}</strong>
            <Button variant="outline" onClick={() => window.location.assign('/offers')}>К предложениям</Button>
          </div>
        ) : (
          <article className="offer-detail-card">
            <div className="offer-detail-card__content">
              <div className="offer-card__topline">
                {offer.venue.avatar_url ? (
                  <img className="offer-venue-avatar" src={offer.venue.avatar_url} alt="" />
                ) : (
                  <span className="offer-venue-avatar offer-venue-avatar--placeholder" aria-label={offer.venue.display_name ?? 'Заведение Хоппера'}>
                    <Icon name="location" />
                  </span>
                )}
                <div className="offer-card__venue">
                  <strong>{offer.venue.display_name ?? 'Заведение Хоппера'}</strong>
                  <span>{offer.venue.address ?? 'Адрес уточняется'}</span>
                </div>
                <span className={`offer-status-pill is-${offer.my_activation?.status ?? offer.state}`}>
                  {offerStateLabel(offer)}
                </span>
              </div>

              <div className="offer-card__body">
                <h1>{offer.title}</h1>
                <p>{offer.description}</p>

                {requirement && (
                  <div className="offer-card__requirement">
                    <div className="offer-card__requirement-copy">
                      <strong>{requirement}</strong>
                      {missing && <span>{missing}</span>}
                    </div>
                    {offer.requirement.percent !== null && (
                      <ProgressBar value={offer.requirement.percent} label="Прогресс до предложения" />
                    )}
                  </div>
                )}

                {activationCopy && (
                  <div className="offer-detail-card__activation">
                    <strong>{activationCopy.title}</strong>
                    <span>{activationCopy.body}</span>
                  </div>
                )}
              </div>

              <div className="offer-card__meta">
                <span>Активировали: {offer.activated_count.toLocaleString('ru-RU')}</span>
                {endDate && <span>До {endDate}</span>}
              </div>
            </div>
          </article>
        )}
      </section>
    </main>
  );
}
